import { FormControl, Select, SelectChangeEvent } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

type CompactSelectSize = "small" | "medium";

type CompactSelectProps = {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  displayEmpty?: boolean;
  renderValue: () => ReactNode;
  children: ReactNode;
  textSize?: CompactSelectSize;
};

const compactFormControlSx =
  (textSize: CompactSelectSize) => (theme: Theme) => ({
    minWidth: 0,
    width: "auto",
    display: "inline-flex",
    "& .MuiSelect-select": {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      lineHeight: textSize === "medium" ? "24px" : "20px",
      py: textSize === "medium" ? 0.25 : 0.5,
      px: textSize === "medium" ? 0.75 : 1,
      borderRadius: `${theme.shape.borderRadius}px`,
      width: "auto",
      minWidth: 0,
      fontSize:
        textSize === "medium"
          ? theme.typography.body1.fontSize
          : theme.typography.pxToRem(13),
      fontWeight:
        textSize === "medium"
          ? theme.typography.body1.fontWeight
          : theme.typography.button.fontWeight,
      letterSpacing:
        textSize === "medium"
          ? theme.typography.body1.letterSpacing
          : theme.typography.button.letterSpacing,
      "&:hover": {
        backgroundColor: "action.hover",
      },
    },
    "& .MuiSelect-icon": {
      fontSize: textSize === "medium" ? "1.5rem" : "1.25rem",
    },
  });

const menuProps = {
  PaperProps: {
    sx: {
      minWidth: "max-content",
    },
  },
  MenuListProps: {
    sx: {
      whiteSpace: "nowrap",
    },
  },
};

export function CompactSelect({
  value,
  onChange,
  ariaLabel,
  displayEmpty = false,
  renderValue,
  children,
  textSize = "small",
}: CompactSelectProps) {
  const handleChange = (event: SelectChangeEvent) => {
    onChange(event.target.value);
  };

  return (
    <FormControl variant="standard" sx={compactFormControlSx(textSize)}>
      <Select
        size="small"
        value={value}
        onChange={handleChange}
        inputProps={{ "aria-label": ariaLabel }}
        disableUnderline
        displayEmpty={displayEmpty}
        MenuProps={menuProps}
        renderValue={renderValue}
      >
        {children}
      </Select>
    </FormControl>
  );
}
